import React from 'react';

// What the shop list says about a shop that is not serving: whether it is
// serving at all, and the voice it says everything else in.
//
// ONE COPY, BECAUSE IT IS ONE RULE. It was extracted when two panels — the
// picker ("shops you can sell from") and the performance report ("how your
// shops perform") — flagged the same two conditions separately, and so could
// come to say different things about the same shop. Those two panels are now
// one list (`ShopList`); the extraction stays because the rule still has a
// second statement to agree with, one layer down.
//
// IT DESCRIBES, IT DOES NOT ACT. A flagged shop is still listed, still
// addable and still removable: the publisher chose it, clicks to it earn
// nothing only while it is locked, and it may well be funded again next week.
// Hiding it would conceal the problem instead of showing it.
//
// The server's scorer applies these same two conditions as a numeric gate
// (`services/merchant-prior/score.ts`, `HEALTH`); this is the sentence, not a
// second opinion — it never decides the order, which arrives from the server.

/** The fields the sentence reads. Deliberately narrower than a shop row. */
export interface ShopHealthFields {
  programmeStatus: string | null;
  funded: boolean | null;
  /** How many times the network showed this shop out of budget in the window. */
  locksInWindow?: number | null;
  lockWindowDays?: number | null;
}

/**
 * The warning to show beside a shop's name, or `null` when there is none.
 *
 * `funded === null` is "the network did not say", never "out of budget": most
 * networks publish no budget signal at all, and reading silence as a fault
 * would flag every shop on them.
 *
 * TWO FACTS, ONE SENTENCE. The row used to carry the current state beside the
 * name ("out of budget") AND a separate line beneath it ("Seen out of budget 2
 * times in the last 90 days"). Measured 2026-09-06 on the live account: 9 rows
 * flagged, 9 with lock history, and the two sets are IDENTICAL — so the phrase
 * "out of budget" was printed twice on every row that had it, on a screen whose
 * stated voice is one sentence of state per row.
 *
 * They are NOT the same claim and are not merged into one: `funded === false` is
 * now, `locksInWindow` is history. The history exists to catch the shop that is
 * funded TODAY and was not last week — which on this account has never happened
 * (0 rows), and is exactly the case that would otherwise read as healthy. That
 * branch keeps its own wording rather than being deleted as unused.
 */
export function shopHealth(shop: ShopHealthFields): string | null {
  const locks = shop.locksInWindow ?? 0;
  const days = shop.lockWindowDays ?? 0;
  // Only say "N times in D days" when both numbers are real; a count with no
  // window is not a fact a publisher can act on.
  const history = locks > 0 && days > 0 ? `${locks}× in the last ${days} days` : null;

  if (shop.programmeStatus && shop.programmeStatus.toLowerCase() !== 'active') {
    // The programme being off is the bigger fact; budget history still travels
    // with it, because "not running" and "unreliable" are different reasons to
    // pass a shop over.
    return history ? `not running right now — out of budget ${history}` : 'not running right now';
  }
  if (shop.funded === false) {
    return history ? `out of budget (${history})` : 'out of budget';
  }
  // Funded now, but it has not always been. The whole reason this field is read.
  if (history) return `was out of budget ${history}`;
  return null;
}

/** Muted, small — the caption voice both panels use for everything but the name. */
export const metaStyle: React.CSSProperties = {
  color: 'var(--muted)', fontSize: '0.85rem',
};

/** The same caption, coloured and weighted, for a health warning. */
export const warningStyle: React.CSSProperties = {
  ...metaStyle, color: 'var(--warn, #8a5a00)', marginLeft: '0.5rem', fontWeight: 600,
};
